// @ts-strict-ignore
import {AttrsSchema, Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {
  trackDataClassificationError,
  trackMissingDatagroupError,
  trackUnsupportedActionError,
} from 'common/report/queries/utils';
import {WarningTheme} from 'common/components/warning/constants';
import {assertUnreachable} from 'common/util/typescript';
import {panel2react} from 'common/util/react';
import {Translatable} from 'common/i18n';
import {IronContextRegistry} from 'common/report/context-registry';

import {promptFeedbackModal} from '../feedback-modal';
import {FeedbackForFeature} from '../feedback-modal/types';
import css from './index.inline.styl';
import {template} from './template';
import {
  EXAMPLE_LINKS,
  WARNING_HERO_BASICS_LINKS,
  WarningHeroReportName,
  WarningHeroTheme,
  WarningTrackingError,
} from './constants';
import DASHBOARDS_EMPTY_IMAGE from './images/empty-dashboards.svg';
import INSIGHTS_EMPTY_IMAGE from './images/empty-insights.svg';
import FUNNELS_EMPTY_IMAGE from './images/empty-funnels.svg';
import LEXICON_EMPTY_IMAGE from './images/empty-lexicon.svg';
import FLOWS_EMPTY_IMAGE from './images/empty-flows.svg';
import EXPERIMENTS_EMPTY_IMAGE from './images/empty-experiments.svg';

export interface WarningHeroJsxProps {
  attrs?: {
    // mp-warning attributes
    'cta-href'?: string;
    //'img-src'?: string --> this one is set from the 'report' attribute

    // custom
    theme: WarningHeroTheme;
    'tracking-error'?: WarningTrackingError;
    report?: WarningHeroReportName;
  };
  params?: {
    headerText?: Translatable;
    bodyText?: Translatable;
    ctaText?: Translatable;
  };
}

export class WarningHero extends Component<
  EmptyObj,
  WarningHeroJsxProps[`attrs`],
  unknown,
  unknown,
  IronContextRegistry,
  WarningHeroJsxProps[`params`]
> {
  static get attrsSchema(): AttrsSchema<WarningHeroJsxProps[`attrs`]> {
    return {
      ...super.attrsSchema,
      'cta-href': `string`,
      theme: {type: `string`, default: WarningHeroTheme.Default, enum: Object.values(WarningHeroTheme)},
      'tracking-error': `string`,
      report: `string`,
    };
  }

  get config() {
    return {
      template,
      useShadowDom: true,
      css,
      contexts: [`i18n` as const],
      params: {
        headerText: String,
        bodyText: String,
        ctaText: String,
      },
      helpers: {
        handleCtaClick: () => this.dispatchEvent(new CustomEvent(`ctaClick`)),
        getImgSrc: () => {
          const theme = this.attr(`theme`);
          const reportName = this.attr(`report`);
          if (theme === WarningHeroTheme.EmptyReport && reportName) {
            switch (reportName) {
              case WarningHeroReportName.Dashboards:
                return DASHBOARDS_EMPTY_IMAGE;
              case WarningHeroReportName.Insights:
                return INSIGHTS_EMPTY_IMAGE;
              case WarningHeroReportName.Impact:
                return INSIGHTS_EMPTY_IMAGE;
              case WarningHeroReportName.Events:
              case WarningHeroReportName.EventsSidebar:
                // this case is only to make assertUnreachable happy but we don't use this code path
                return INSIGHTS_EMPTY_IMAGE;
              case WarningHeroReportName.Funnels:
                return FUNNELS_EMPTY_IMAGE;
              case WarningHeroReportName.Retention:
                return FUNNELS_EMPTY_IMAGE;
              case WarningHeroReportName.LookupTables:
                return LEXICON_EMPTY_IMAGE;
              case WarningHeroReportName.Flows:
                return FLOWS_EMPTY_IMAGE;
              case WarningHeroReportName.ExperimentReporting:
                return EXPERIMENTS_EMPTY_IMAGE;
              case WarningHeroReportName.PublicDashboards:
                return DASHBOARDS_EMPTY_IMAGE;
              case WarningHeroReportName.Signal:
                return EXPERIMENTS_EMPTY_IMAGE;
              case WarningHeroReportName.Users:
                return INSIGHTS_EMPTY_IMAGE;
              default:
                return assertUnreachable(reportName);
            }
          }
          return ``;
        },
        getTheme: (): WarningTheme => {
          const theme = this.attr(`theme`);
          if (Object.values(WarningTheme).includes(theme as WarningTheme)) {
            return theme as WarningTheme;
          }
          return WarningTheme.Default;
        },
        getBodyText: () => {
          const {t, print} = this.getContext(`i18n`);

          if (this.params.bodyText) {
            return print(this.params.bodyText);
          } else if (this.attr(`theme`) === WarningHeroTheme.EmptyReport) {
            const reportName = this.attr(`report`);
            if (reportName) {
              const basicLink = WARNING_HERO_BASICS_LINKS[reportName];
              const exampleLink = EXAMPLE_LINKS[reportName];
              const interpolation = {escapeValue: false};
              switch (reportName) {
                case WarningHeroReportName.Dashboards:
                  return t(`boards.empty.body`, {basicLink, exampleLink, interpolation});
                case WarningHeroReportName.Insights:
                  return t(`insights.empty.body`, {basicLink, exampleLink, interpolation});
                case WarningHeroReportName.Funnels:
                  return t(`funnels.empty.body`, {basicLink, exampleLink, interpolation});
                case WarningHeroReportName.Impact:
                  return t(`impact.empty.body`, {basicLink, interpolation});
                case WarningHeroReportName.Retention:
                  return t(`retention.empty.body`, {basicLink, exampleLink, interpolation});
                case WarningHeroReportName.Flows:
                  return t(`flows.empty.body`, {basicLink, exampleLink, interpolation});
                case WarningHeroReportName.ExperimentReporting:
                  return t(`experiment-reporting.empty.body`, {basicLink, interpolation});
                case WarningHeroReportName.LookupTables:
                  return t(`lookup-tables.empty.body`, {basicLink, exampleLink, interpolation});
                case WarningHeroReportName.PublicDashboards:
                  return t(`public-boards.empty.body`);
                case WarningHeroReportName.Signal:
                  return t(`signal.empty.body`, {basicLink, interpolation});
                case WarningHeroReportName.Events:
                case WarningHeroReportName.EventsSidebar:
                  // this case is only to make assertUnreachable happy but we don't use this code path
                  // TODO: add a help doc for new liveview like others
                  return `Please try a different query`;
                case WarningHeroReportName.Users:
                  return t(`users.empty.body`);
                default:
                  return assertUnreachable(reportName);
              }
            }
          }

          return ``;
        },
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();
    const theme = this.attr(`theme`);
    const trackingError = this.attr(`tracking-error`);
    const reportName = this.attr(`report`);
    if (theme === WarningHeroTheme.Sensitive && reportName) {
      trackDataClassificationError(reportName);
    }
    if (
      theme === WarningHeroTheme.Error &&
      trackingError === WarningTrackingError.MissingMappedDatagroup &&
      reportName
    ) {
      trackMissingDatagroupError(reportName);
    }
    if (
      theme === WarningHeroTheme.Error &&
      trackingError === WarningTrackingError.UnsupportedActionError &&
      reportName
    ) {
      trackUnsupportedActionError(reportName, this.getContext(`i18n`).print(this.params.bodyText));
    }
  }

  handleFeedBackButtonClicked = () => {
    promptFeedbackModal({
      feature: FeedbackForFeature.BehaviorMetrics,
    });
  };

  handleHide = () => {
    window.localStorage.setItem(`hide-new-ui-report-hero`, `true`);
    // HACK: just re-render the component so it's "hidden"
    // TODO: https://mixpanel.atlassian.net/browse/AN-409
    // remove hide/show empty report once we release report_consistency to 100%
    this.setAttribute(`theme`, WarningHeroTheme.EmptyReport);
  };
}

export const WarningHeroEl = defineAndMakeJsxFactory<WarningHeroJsxProps>(`mp-warning-hero`, WarningHero);
export const WarningHeroReact = panel2react<WarningHeroJsxProps>(`mp-warning-hero`, WarningHero);
